import dayjs from "dayjs";
import { toast } from "sonner";
import { Icon } from "@iconify/react";
import type { Folder } from "@/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useGetFolderById } from "@/hooks/folder";
import { formStore } from "@/store/form/form-store";
import { useDuplicateForm } from "@/hooks/form/use-form";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useBreadcrumbStore } from "@/store/ui";
import { Table } from "@/components/table";
import { useAuthStore } from "@/store/use-auth-store";
import {
  AddFormNameModal,
  FormNameModal,
} from "@/components/form/add-edit-form-name-modal";
import BulkUploadModal from "@/components/form/bulk-upload-modal";
import DownloadDataModal from "@/components/form/download-data-modal";
import DeleteDataModal from "@/components/form/delete-data-modal";
import DeleteFormModal from "@/components/form/delete-form-modal";
import { MoveFormModal } from "@/components/form/move-form-modal";
import { formService } from "@/services/form";

const FormsInFolder = () => {
  const { t } = useTranslation();
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const { setFormList } = formStore();
  const { roles } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFormName, setSelectedFormName] = useState<any>(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [selectedFormForBulk, setSelectedFormForBulk] = useState<any>(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [selectedFormForMove, setSelectedFormForMove] = useState<any>(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedFormForDownload, setSelectedFormForDownload] = useState<any>(null);
  const [isDeleteDataModalOpen, setIsDeleteDataModalOpen] = useState(false);
  const [selectedFormForDeleteData, setSelectedFormForDeleteData] = useState<any>(null);
  const [isDeleteFormModalOpen, setIsDeleteFormModalOpen] = useState(false);
  const [selectedFormForDeleteForm, setSelectedFormForDeleteForm] = useState<any>(null);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [selectedFormForDuplicate, setSelectedFormForDuplicate] = useState<any>(null);
  const duplicateFormMutation = useDuplicateForm();

  const { data: folderData, isPending: isFolderPending } = useGetFolderById(
    folderId || ""
  );
  const { setCustomBreadcrumbItems } = useBreadcrumbStore();

  useEffect(() => {
    if (folderData?.forms) setFormList(folderData.forms);
  }, [folderData, setFormList]);

  // Set custom breadcrumbs with folder name
  useEffect(() => {
    if (folderData) {
      setCustomBreadcrumbItems([
        { name: t('sidebar.home'), href: '/' },
        { name: t('sidebar.forms'), href: '/admin/form' },
        { name: folderData.name, href: '' }
      ]);
    } else {
      setCustomBreadcrumbItems(null);
    }
  }, [folderData, t, setCustomBreadcrumbItems]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setCustomBreadcrumbItems(null);
    };
  }, [setCustomBreadcrumbItems]);

  // Use forms directly from folder response
  const folderForms = folderData?.forms || [];

  const openModal = (formName: any) => {
    setSelectedFormName(formName);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedFormName(null);
  };

  const openBulkModal = (form: any) => {
    setSelectedFormForBulk(form);
    setIsBulkModalOpen(true);
  };

  const closeBulkModal = () => {
    setIsBulkModalOpen(false);
    setSelectedFormForBulk(null);
  };

  const openMoveModal = (form: any) => {
    setSelectedFormForMove(form);
    setIsMoveModalOpen(true);
  };

  const closeMoveModal = () => {
    setIsMoveModalOpen(false);
    setSelectedFormForMove(null);
  };

  const openDownloadModal = (form: any) => {
    setSelectedFormForDownload(form);
    setIsDownloadModalOpen(true);
  };

  const closeDownloadModal = () => {
    setIsDownloadModalOpen(false);
    setSelectedFormForDownload(null);
  };

  const openDeleteDataModal = (form: any) => {
    setSelectedFormForDeleteData(form);
    setIsDeleteDataModalOpen(true);
  };

  const closeDeleteDataModal = () => {
    setIsDeleteDataModalOpen(false);
    setSelectedFormForDeleteData(null);
  };

  const openDeleteFormModal = (form: any) => {
    setSelectedFormForDeleteForm(form);
    setIsDeleteFormModalOpen(true);
  };

  const closeDeleteFormModal = () => {
    setIsDeleteFormModalOpen(false);
    setSelectedFormForDeleteForm(null);
  };

  const openDuplicateDialog = (form: any) => {
    setSelectedFormForDuplicate(form);
    setIsDuplicateDialogOpen(true);
  };

  const closeDuplicateDialog = () => {
    setIsDuplicateDialogOpen(false);
    setSelectedFormForDuplicate(null);
  };

  const handleDuplicate = async () => {
    if (!selectedFormForDuplicate) return;
    
    try {
      const response = await duplicateFormMutation.mutateAsync({
        formId: selectedFormForDuplicate.id,
        newName: `${selectedFormForDuplicate.name} (Copy)`,
      });
      if (response) {
        toast.success(t('common.duplicateFormSuccessfully'));
        closeDuplicateDialog();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("formManagement.updatingFormNameFailed"));
    }
  };

  const handleDownloadStructure = async (form: any) => {
    try {
      const response = await formService.downloadFormStructure(form.id);
      if (response) {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${form.name}_structure.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(t('common.downloadStructureSuccessfully'));
      }
    } catch (error) {
      console.error(error);
      toast.error(t('common.failedToDownloadStructure'));
    }
  };

  const formattedFormNames = useMemo(() => {
    if (!folderForms || !Array.isArray(folderForms)) return [];
    return folderForms.map((form: any) => ({
      ...form,
      createdByDisplay: folderData?.creator
        ? `${folderData.creator.firstName || ""} ${folderData.creator.lastName || ""}`
        : "Unknown",
      updatedAtDisplay: form.updatedAt
        ? dayjs(form.updatedAt).format("MMM D, YYYY, h:mm A")
        : "",
    }));
  }, [folderForms, folderData]);

  const columns = [
    {
      key: "name",
      label: t("formManagement.formName"),
      width: 250,
      minWidth: 200,
      maxWidth: 400,
    },
    {
      key: "type",
      label: t("formManagement.type"),
      width: 120,
      minWidth: 100,
      maxWidth: 150,
    },
    {
      key: "status",
      label: t("formManagement.status"),
      width: 120,
      minWidth: 100,
      maxWidth: 150,
    },
    {
      key: "createdByDisplay",
      label: t("formManagement.createdBy"),
      width: 180,
      minWidth: 150,
      maxWidth: 250,
    },
    {
      key: "updatedAtDisplay",
      label: t("formManagement.updatedAt"),
      width: 200,
      minWidth: 180,
      maxWidth: 250,
    },
    {
      key: "actions",
      label: t("formManagement.actions"),
      width: 150,
      minWidth: 120,
      maxWidth: 180,
      resizable: false,
    },
  ];

  const tableData = useMemo(() => {
    if (!Array.isArray(formattedFormNames)) return [];
    return formattedFormNames.map((form: any) => ({
      ...form,
      actions: (
        <div className="flex items-center gap-2">
          <button className="text-textIcon" onClick={() => openModal(form)}>
            <Icon icon="ic:baseline-edit" fontSize={18} />
          </button>
          <Link to={`/admin/form/design/${form.id}`} className="text-textIcon">
            <Icon
              icon={form.design ? "mdi:file-document-edit" : "gridicons:create"}
              fontSize={18}
            />
          </Link>
          {form.design && (
            <Link to={`/admin/form/${form.id}`} className="text-textIcon">
              <Icon icon="icon-park-solid:preview-open" fontSize={18} />
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-textIcon">
                <Icon icon="ic:baseline-more-vert" fontSize={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-40">
              <DropdownMenuItem onClick={() => openMoveModal(form)}>
                <Icon
                  icon="material-symbols:drive-file-move"
                  className="mr-2 h-4 w-4"
                />
                {t("formManagement.move")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openDuplicateDialog(form)}>
                <Icon
                  icon="ic:baseline-content-copy"
                  className="mr-2 h-4 w-4"
                />
                {t("formManagement.duplicate")}
              </DropdownMenuItem>
              {roles?.includes('Admin') && (
                <>
                  <DropdownMenuItem onClick={() => openBulkModal(form)}>
                    <Icon icon="bytesize:upload" className="mr-2 h-4 w-4" />
                    Bulk Upload
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openDownloadModal(form)}>
                    <Icon icon="bytesize:download" className="mr-2 h-4 w-4" />
                    {t('common.downloadData')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openDeleteDataModal(form)}>
                    <Icon icon="bytesize:trash" className="mr-2 h-4 w-4" />
                    {t('common.deleteData')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDownloadStructure(form)}>
                    <Icon icon="bytesize:download" className="mr-2 h-4 w-4" />
                    {t('common.downloadStructure')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openDeleteFormModal(form)}>
                    <Icon icon="bytesize:trash" className="mr-2 h-4 w-4" />
                    {t('common.deleteForm')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    }));
  }, [formattedFormNames, t, duplicateFormMutation]);

  if (isFolderPending) {
    return (
      <div className="h-full flex justify-center items-center">
        <div className="flex items-center gap-2">
          <Icon icon="eos-icons:loading" className="text-xl" />
          <span>{t("common.loading")}</span>
        </div>
      </div>
    );
  }

  if (!folderData) {
    return (
      <div className="text-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Icon
            icon="material-symbols:folder-off"
            className="text-6xl text-gray-400"
          />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {t("folders.folderNotFound")}
            </h3>
            <p className="text-gray-500">
              {t("folders.folderNotFoundDescription")}
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate(-1)} variant="outline">
              <Icon icon="material-symbols:arrow-back" className="mr-2" />
              {t("common.goBack")}
            </Button>
            <Button onClick={() => navigate("/admin/form")} variant="outline">
              <Icon icon="material-symbols:home" className="mr-2" />
              {t("folders.backToFolders")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const folder: Folder = folderData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/form")}
          >
            <Icon icon="material-symbols:arrow-back" className="" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Icon
                icon="material-symbols:folder"
                className="text-2xl text-darkBlue"
              />
              {folder.name}
            </h1>
            {folder.description && (
              <p className="text-gray-600 mt-1">{folder.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Forms Table */}
      <div>
        <Table
          paginate
          exportable
          data={tableData}
          columns={columns}
          title={`${folder.name} - ${t("formManagement.formNames")}`}
          exportData={tableData}
          additionalButton={
            <>
              <AddFormNameModal folderId={folderId} />
              <Link to={`/admin/form/upload-track?folderId=${folderId}`}>
                <button
                  type="submit"
                  className="main-dark-button !px-4 float-right"
                >
                  <Icon icon={"bytesize:upload"} className="text-white text-xl" />
                </button>
              </Link>
            </>
          }
        />
        {selectedFormName && (
          <FormNameModal
            mode="edit"
            isOpen={isModalOpen}
            onClose={closeModal}
            formData={selectedFormName}
            onSuccess={closeModal}
          />
        )}
        {selectedFormForBulk && (
          <BulkUploadModal
            isOpen={isBulkModalOpen}
            onClose={closeBulkModal}
            formId={selectedFormForBulk.id}
          />
        )}
        {selectedFormForMove && (
          <MoveFormModal
            isOpen={isMoveModalOpen}
            onClose={closeMoveModal}
            formId={selectedFormForMove.id}
            formName={selectedFormForMove.name}
            currentFolderId={folderId}
            onSuccess={closeMoveModal}
          />
        )}
        {selectedFormForDownload && (
          <DownloadDataModal
            isOpen={isDownloadModalOpen}
            onClose={closeDownloadModal}
            formId={selectedFormForDownload.id}
          />
        )}
        {selectedFormForDeleteData && (
          <DeleteDataModal
            isOpen={isDeleteDataModalOpen}
            onClose={closeDeleteDataModal}
            formId={selectedFormForDeleteData.id}
          />
        )}
        {selectedFormForDeleteForm && (
          <DeleteFormModal
            isOpen={isDeleteFormModalOpen}
            onClose={closeDeleteFormModal}
            formId={selectedFormForDeleteForm.id}
            formName={selectedFormForDeleteForm.name}
          />
        )}

        <AlertDialog open={isDuplicateDialogOpen} onOpenChange={setIsDuplicateDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('common.duplicateFormConfirmation')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('common.duplicateFormDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={closeDuplicateDialog}>
                {t('common.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDuplicate}
                disabled={duplicateFormMutation.isPending}
              >
                {duplicateFormMutation.isPending ? t('common.saving') : t('common.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default FormsInFolder;
